"use client";

/**
 * @fileoverview Client-side authentication utilities for the web application.
 *
 * This module provides authentication client instances for browser use.
 * It uses better-auth with magic link support
 * and handles cookie-based session management through an API proxy.
 *
 * @module auth-client
 *
 * @example Browser-side authentication flow
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * // Sign in with email/password
 * const result = await authClient.signIn.email({
 *   email: "user@example.com",
 *   password: "securePassword123",
 * });
 *
 * // Sign in with magic link
 * await authClient.signIn.magicLink({
 *   email: "user@example.com",
 *   callbackURL: "/dashboard",
 * });
 *
 * // Sign out
 * await authClient.signOut();
 *
 * // Get current session
 * const session = await authClient.getSession();
 * ```
 *
 * @example React hook usage
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * function AuthButton() {
 *   const { data: session, isPending } = authClient.useSession();
 *
 *   if (isPending) return <Spinner />;
 *   if (session) return <LogoutButton />;
 *   return <LoginButton />;
 * }
 * ```
 */

import { magicLinkClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

/**
 * Type definition for the authenticated client with magic link plugin support.
 *
 * @description This type represents the full better-auth client instance
 * configured with the magic link plugin. It includes all authentication
 * methods like signIn, signOut, useSession, and magic link specific methods.
 *
 * @typedef {ReturnType<typeof createAuthClient<{ plugins: [ReturnType<typeof magicLinkClient>] }>>} AuthClientWithPlugins
 *
 * @property {object} signIn - Sign-in methods (email, magicLink, etc.)
 * @property {Function} signOut - Sign out the current user
 * @property {Function} useSession - React hook for session state
 * @property {Function} getSession - Get current session (async)
 */
type AuthClientWithPlugins = ReturnType<
  typeof createAuthClient<{ plugins: [ReturnType<typeof magicLinkClient>] }>
>;

/**
 * Cached browser client instance - created lazily on first use in browser.
 * @internal
 */
let _authClient: AuthClientWithPlugins | null = null;

/**
 * Get the auth client for browser use (lazy singleton pattern).
 *
 * @description Creates and caches a better-auth client instance configured for
 * browser-side authentication. The client uses the same-origin `/api/auth` proxy
 * to ensure cookies are properly set and forwarded from the backend server.
 *
 * **Important:** This function MUST only be called in browser context (client components).
 * For server-side session validation, use `ssrAuthClient` instead.
 *
 * @returns {AuthClientWithPlugins | null} The configured authentication client instance, or null during SSR
 *
 * @example
 * ```typescript
 * // In a client component
 * "use client";
 * import { getAuthClient } from "@/lib/auth-client";
 *
 * function LoginForm() {
 *   const handleLogin = async () => {
 *     const client = getAuthClient();
 *     if (!client) return; // SSR guard
 *     await client.signIn.email({
 *       email: "user@example.com",
 *       password: "password123",
 *     });
 *   };
 *   // ...
 * }
 * ```
 *
 * @see {@link authClient} for the proxy-based convenience export
 * @see {@link ssrAuthClient} for server-side usage
 */
export function getAuthClient(): AuthClientWithPlugins | null {
  if (_authClient) {
    return _authClient;
  }

  if (typeof window === "undefined") {
    // Return null during SSR - hooks will return pending state
    return null;
  }

  _authClient = createAuthClient({
    baseURL: `${window.location.origin}/api/auth`,
    fetchOptions: {
      credentials: "include",
    },
    plugins: [magicLinkClient()],
  });

  return _authClient;
}

/**
 * SSR-safe stub that mimics the authClient interface during server rendering.
 * Returns loading/pending states for all methods and hooks.
 * @internal
 */
const ssrStub: AuthClientWithPlugins = {
  useSession: () => ({ data: null, isPending: true, error: null }),
  getSession: async () => ({ data: null, error: null }),
  signIn: {
    email: async () => ({ data: null, error: null }),
    magicLink: async () => ({ data: null, error: null }),
  },
  signOut: async () => ({ data: null, error: null }),
  signUp: {
    email: async () => ({ data: null, error: null }),
  },
} as unknown as AuthClientWithPlugins;

/**
 * Auth client for browser use - lazy initialized via Proxy pattern.
 *
 * @description A convenience export that lazily creates the authentication client
 * on first property access. This allows importing the client at module level
 * while deferring actual initialization until runtime in the browser.
 *
 * The Proxy intercepts all property accesses and forwards them to the real client
 * obtained via `getAuthClient()`. During SSR, it returns a stub that mimics the
 * interface with pending/loading states.
 *
 * @type {AuthClientWithPlugins}
 *
 * @example Sign in with email/password
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * const { data, error } = await authClient.signIn.email({
 *   email: "user@example.com",
 *   password: "securePassword123",
 * });
 *
 * if (error) {
 *   console.error("Login failed:", error.message);
 * } else {
 *   console.log("Logged in as:", data.user.email);
 * }
 * ```
 *
 * @example Sign in with magic link (passwordless)
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * await authClient.signIn.magicLink({
 *   email: "user@example.com",
 *   callbackURL: "/dashboard",
 * });
 * // User receives email with login link
 * ```
 *
 * @example Use session hook in React component
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * function UserProfile() {
 *   const { data: session, isPending, error } = authClient.useSession();
 *
 *   if (isPending) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!session) return <LoginPrompt />;
 *
 *   return <div>Welcome, {session.user.name}!</div>;
 * }
 * ```
 *
 * @example Sign out current user
 * ```typescript
 * import { authClient } from "@/lib/auth-client";
 *
 * async function handleLogout() {
 *   await authClient.signOut();
 *   // User is now logged out, redirect to home
 *   window.location.href = "/";
 * }
 * ```
 *
 * @see {@link getAuthClient} for the underlying factory function
 * @see {@link ssrAuthClient} for server-side session validation
 */
export const authClient = new Proxy({} as AuthClientWithPlugins, {
  get(_target, prop) {
    // Lazily get/create the real client, or use stub during SSR
    const realClient = getAuthClient();
    const client = realClient !== null ? realClient : ssrStub;
    const value = Reflect.get(client, prop, client);
    return value;
  },
});
